import { registerPendingCounter } from './session-lifecycle.ts';
import { useSyncExternalStore } from 'react';
import { ApiError, NetworkError, getUser, onSessionReset, sessionEpoch, post } from './api.ts';
import { invalidateMoney } from './query.ts';

/**
 * Operations created without network wait here (in localStorage) and are sent when the
 * server is reachable again. Each carries its clientId, so a retry after a lost response
 * does not create a duplicate on the server.
 */
export interface Pending {
  clientId: string;
  path: '/transactions' | '/transfers';
  body: Record<string, unknown>;
  /** shown to the user, e.g. «−250 ₽, Продукты» */
  label: string;
  createdAt: string;
  userId: number;
  /** the server rejected it; kept for the user to see instead of being dropped silently */
  error?: string;
}

const KEY = 'fin.queue.v1';
const subs = new Set<() => void>();

function read(): Pending[] {
  try { return JSON.parse(localStorage.getItem(KEY) ?? '[]') as Pending[]; } catch { return []; }
}
let queue: Pending[] = read();

function commit(next: Pending[]) {
  queue = next;
  try { localStorage.setItem(KEY, JSON.stringify(queue)); } catch { /* storage unavailable: the queue lives until reload */ }
  subs.forEach((s) => s());
}

export const useQueue = () => useSyncExternalStore((cb) => { subs.add(cb); return () => { subs.delete(cb); }; }, () => queue);

export function enqueue(item: Omit<Pending, 'createdAt' | 'userId'>) {
  const user = getUser();
  if (!user) throw new NetworkError();
  commit([...queue.filter((p) => p.clientId !== item.clientId), { ...item, createdAt: new Date().toISOString(), userId: user.id }]);
}

export function discard(clientId: string) {
  commit(queue.filter((p) => p.clientId !== clientId));
}

const sendable = () => {
  const user = getUser();
  return user ? queue.filter((p) => p.userId === user.id && !p.error) : [];
};
export const pendingCount = () => { const user = getUser(); return user ? queue.filter((p) => p.userId === user.id).length : 0; };
onSessionReset(() => commit([]));
export const hasPending = () => sendable().length > 0;

let flushing: Promise<number> | null = null;
/** send waiting operations in order; stops at the first network failure. Resolves to the number sent. */
export function flushQueue(): Promise<number> {
  flushing ??= (async () => {
    const started = sessionEpoch();
    let sent = 0;
    for (const item of sendable()) {
      if (started !== sessionEpoch() || getUser()?.id !== item.userId) break;
      try {
        await post(item.path, item.body);
        if (started !== sessionEpoch()) break;
        commit(queue.filter((p) => p.clientId !== item.clientId));
        sent++;
      } catch (e) {
        if (started !== sessionEpoch()) break;
        // no network, expired session or a server failure: try again later
        if (e instanceof NetworkError || !(e instanceof ApiError) || e.status === 401 || e.status >= 500) break;
        commit(queue.map((p) => (p.clientId === item.clientId ? { ...p, error: e.message } : p)));
      }
    }
    if (sent) invalidateMoney();
    return sent;
  })().finally(() => { flushing = null; });
  return flushing;
}

registerPendingCounter(pendingCount);
